<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold"
        >Places ({{ listMeta.total }})</v-toolbar-title
      >

      <v-spacer></v-spacer>
      <v-row v-show="showSearch" align="center" style="max-width: 300px;">
        <v-text-field
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search place"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-btn class="ml-5" icon @click="showSearchField">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>

        <v-btn class="ml-5" color="primary" dark :to="{ name: 'places.create' }"
          >Create Place</v-btn
        >
      </template>
    </v-app-bar>

    <div
      class="mt-12 mx-4"
      id="place-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
    >
      <v-simple-table>
        <thead>
          <tr>
            <th width="25%" colspan="2" class="text-left pa-0">Name</th>
            <th width="25%" class="text-left">Type</th>
            <th width="25%" class="text-left">Location</th>
            <th width="25%" class="text-left">Added Date</th>
            <th width="25%" class="text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="place in places" :key="place.id">
            <td colspan="2">
              {{ place.name }}
            </td>
            <td>
              {{ place.type.charAt(0).toUpperCase() + place.type.slice(1) }}
            </td>
            <td>
              {{ place.places_address }}
            </td>
            <td>{{ place.addedDate }}</td>
            <td>
              <v-btn
                color="primary"
                class="ma-1"
                fab
                small
                @click="
                  $router.push({
                    name: 'places.update',
                    params: { id: place.id }
                  })
                "
              >
                <v-icon small>{{ icons.edit }}</v-icon>
              </v-btn>

              <v-btn
                v-if="!place.in_used"
                class="ma-1"
                color="error"
                fab
                small
                @click="showDeleteDialog(place.id)"
              >
                <v-icon small>{{ icons.delete }}</v-icon>
              </v-btn>

              <v-tooltip v-else bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-on="on">
                    <v-btn
                      class="ma-1"
                      color="error"
                      fab
                      small
                      v-bind="attrs"
                      disabled
                    >
                      <v-icon small>{{ icons.delete }}</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>Data is already in used</span>
              </v-tooltip>
            </td>
          </tr>

          <template v-if="loading">
            <tr v-for="index in 10" :key="index + '-skeleton'">
              <td class="py-3 pl-0" width="5%">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>
    </div>

    <ConfirmModal
      v-model="showDeleteModal"
      title="Delete Place"
      :message="deleteMessage"
      @cancel="showDeleteModal = false"
      @confirm="deletePlaceDetail"
    ></ConfirmModal>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiFileExcel,
  mdiPencil,
  mdiTrashCanOutline
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'
import ConfirmModal from '@/components/modals/ConfirmModal'

export default {
  name: 'PlaceList',

  mixins: [ControlsMixin],

  directives: {
    infiniteScroll
  },

  components: {
    AppBarNavIcon,
    ConfirmModal
  },

  data() {
    return {
      loading: false,
      showSearch: false,
      showProfile: false,
      selectedProfile: null,
      filter: {
        search: ''
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        export: mdiFileExcel,
        edit: mdiPencil,
        delete: mdiTrashCanOutline
      },
      showDeleteModal: false,
      deletePlaceId: null
    }
  },

  created() {
    this.clearPlaces()
    this.fetchPlaces(1)
  },

  computed: {
    ...mapState({
      places: state => state.place.list,
      listMeta: state => state.place.listMeta
    }),

    paginationLength() {
      return (this.listMeta.total / this.listMeta.per_page) | 0
    },

    deleteMessage() {
      return 'Are you sure you want to delete this record?'
    }
  },

  methods: {
    ...mapActions({
      getPlaces: 'place/getPlaces',
      deletePlace: 'place/deletePlace',
      setAlert: 'alert/setAlert'
    }),

    ...mapMutations({
      clearPlaces: 'place/clearPlaceList'
    }),

    pageChanged(page) {
      this.fetchPlaces(page)
    },

    loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        this.fetchPlaces(this.listMeta.current_page + 1)
      }
    },

    search: debounce(function() {
      this.clearPlaces()
      this.fetchPlaces()
    }, 600),

    async fetchPlaces(page = 1) {
      if (this.loading) return
      let params = { page }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      this.loading = true
      await this.getPlaces(params)
      this.loading = false
    },

    async deletePlaceDetail() {
      this.deletePlace(this.deletePlaceId)
        .then(() => {
          this.showDeleteModal = false
          this.deletePlaceId = null
          this.clearPlaces()
          this.fetchPlaces(1)
          this.setAlert({
            message: 'Place successfully deleted!',
            color: 'success'
          })
        })
        .catch(err => {
          this.setAlert({
            message: err.response.data.message,
            color: 'error'
          })
        })

      return true
    },

    showDeleteDialog(placeId) {
      this.showDeleteModal = true
      this.deletePlaceId = placeId
    },

    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    }
  }
}
</script>
